import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { BellRing, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TicketsSoonNotify({ eventId, compact = false }) {
  const [info, setInfo] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/events/${eventId}/notify-info`).then(r => setInfo(r.data)).catch(() => setInfo({ open: false }));
  }, [eventId]);

  if (!info) return null;

  const join = async () => {
    if (!email.includes('@')) return toast.error('Enter a valid email');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/notify-signup`, { name, email });
      setInfo(i => ({ ...i, count: r.data.count }));
      setDone(true);
      toast.success(r.data.already ? "You're already on the list!" : "You're on the list — we'll email you first");
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not sign you up'); }
    finally { setBusy(false); }
  };

  const heading = compact ? 'text-indigo-700' : 'text-indigo-300';
  const sub = compact ? 'text-slate-500' : 'text-muted-foreground';
  const proof = compact ? 'text-indigo-600' : 'text-indigo-400';

  if (!info.open) {
    return <p className={`text-sm text-center py-4 ${sub}`} data-testid="tickets-soon-fallback">Tickets aren't available right now.</p>;
  }

  return (
    <div className={`rounded-xl border p-4 space-y-2.5 text-center ${compact ? 'border-indigo-200 bg-indigo-50' : 'border-indigo-500/40 bg-indigo-500/5'}`} data-testid="tickets-soon-notify">
      <p className={`font-bold flex items-center justify-center gap-1.5 ${heading}`}>
        <BellRing className="h-4 w-4" /> Tickets will be on sale soon
      </p>
      <p className={`text-xs ${sub}`}>Be first in line — we'll email you the moment tickets drop.</p>
      {done ? (
        <p className="text-sm font-semibold text-emerald-500 flex items-center justify-center gap-1.5" data-testid="tickets-soon-done">
          <CheckCircle2 className="h-4 w-4" /> You're on the list!
        </p>
      ) : (
        <div className="space-y-2">
          <Input placeholder="Your name (optional)" value={name} onChange={e => setName(e.target.value)}
            className={compact ? 'bg-white text-slate-900' : ''} data-testid="tickets-soon-name" />
          <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)}
            className={compact ? 'bg-white text-slate-900' : ''} data-testid="tickets-soon-email" />
          <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={join} data-testid="tickets-soon-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Notify me when tickets drop'}
          </Button>
        </div>
      )}
      <p className={`text-[11px] font-semibold ${proof}`} data-testid="tickets-soon-count">
        🔔 {info.count + (done ? 0 : 0)} {info.count === 1 ? 'person' : 'people'} will be notified when tickets go public
      </p>
    </div>
  );
}
